import { ProjectVO } from '@project/model/vo/project.vo';
import { ProjectModel } from '@project/model/project.model';
import { ProjectGroupVO } from '@project/model/vo/project-group.vo';
import { ProjectUtils } from '@project/components/project/util/project-utils';
import { AlertHelper } from '@shared/alert/alert.helper';
import { Injectable } from '@angular/core';
import { Log } from '@shared/debug/log';
import { OBJRepresentationDataVO, RepresentationDataVO } from '@shared/vo/representation.vo';
import { ProjectNavigationPresenter } from '@project-navigation/project-navigation.presenter';
import { ImportedCaeProjectDTO } from '../../../model/vo/imported-cae-project.dto';
import { ProjectGroupUtils } from '../util/project-group-utils';
import { ProjectNavigationModel } from '@project-navigation/project-navigation.model';
import { UserSettings } from '@user/model/user-settings';
import { SaveCaeButton, SaveCaeProjectComponent } from './save-cae-project/save-cae-project.component';
import { ProjectChangedType } from '../../../model/project-changed';
import { WindowService } from '@shared/window/window.service';
import { RepresentationService } from '../../../service/representation.service';
import { CaeDataService, SaveImportedCaeProjectGroupDTO, UpdateImportedCaeProjectGroupDTO } from '../../../cae/cae-data.service';

@Injectable({ providedIn: "root" })
export class SaveCaeProjectService {
  constructor(
    private projectModel: ProjectModel,
    private alertHelper: AlertHelper,
    private projectNavigationPresenter: ProjectNavigationPresenter,
    private userSettings: UserSettings,
    private windowService: WindowService,
    private representationService: RepresentationService,
    private caeDataService: CaeDataService,
  ) {}

  public async saveCurrentProject() {
    try {
      const projectGroup =
        await this.projectNavigationPresenter.openImportCaeDialog();
      this.representationService.update();
      const representationData: RepresentationDataVO =
        ProjectUtils.findFirstView3DRepresentationData(
          this.projectModel.currentProject,
        );
      const dto: ImportedCaeProjectDTO = new ImportedCaeProjectDTO();
      dto.project = this.projectModel.currentProject;
      dto.data = representationData as OBJRepresentationDataVO;
      await this.saveMultipleProjects(projectGroup, [dto]);
    } catch (error) {
      Log.info("Project navigation action did not finish");
    }
  }

  public async saveMultipleProjects(
    projectGroup: ProjectGroupVO,
    projects: ImportedCaeProjectDTO[],
  ) {
    if (!projectGroup) {
      return;
    }

    const existingGroup: ProjectGroupVO | undefined =
      this.findExistingProjectWithSameName(projectGroup);
    const overwrite = await this.validateOverwrite(existingGroup, projects);
    if (overwrite === SaveCaeButton.CANCEL) {
      return;
    }

    try {
      let project: ProjectVO;
      if (existingGroup && overwrite === SaveCaeButton.UPDATE_EXISTING) {
        project = await this.updateExistingCaeProject(projects, existingGroup);
      } else {
        project = await this.saveImportedCaeProject(projectGroup, projects);
      }
      this.userSettings.saveLastSelectedProjectFromProjectVO(project, false);
      this.updateProjectDataLoaded(project);
      // Call rename to trigger projectChanged with type ProjectRenamed.
      this.projectModel.rename(project, project.name);
      this.projectModel.resetProjectChanged();
      this.projectModel.sendProjectChanged(ProjectChangedType.CAEProjectSaved);
    } catch (error) {
      Log.error(error);
      await this.alertHelper.showError(
        "importProject.title.import_failed",
        "importProject.message.import_failed",
      );
    }
  }

  private async saveImportedCaeProject(projectGroup: ProjectGroupVO, projects: ImportedCaeProjectDTO[]) {
    let parentGroupId: number | undefined = ProjectGroupUtils.isConfigurationGroup(projectGroup)
      ? projectGroup.parentGroup.id
      : projectGroup.id;
    if (
      parentGroupId === ProjectNavigationModel.ROOT_USER_ID ||
      parentGroupId === ProjectNavigationModel.ROOT_ID
    ) {
      parentGroupId = undefined;
    }
    const dto: SaveImportedCaeProjectGroupDTO = {
      importedCaeProjects: projects,
      parentGroupId: parentGroupId,
      projectGroupName: ProjectGroupUtils.isConfigurationGroup(projectGroup)
        ? projectGroup.groupName
        : this.projectModel.currentProject.projectGroup.groupName,
      projectGroupGuid: this.projectModel.currentProject.projectGroup.guid,
      importType: ProjectGroupVO.CAE_IMPORT_TYPE,
      currentProjectName: this.projectModel.currentProject.name,
    }
    return await this.caeDataService.saveImportedCaeProjectGroup(dto);
  }

  private async updateExistingCaeProject(projects: ImportedCaeProjectDTO[], existingGroup: ProjectGroupVO) {
    const dto: UpdateImportedCaeProjectGroupDTO = {
      importedCaeProjects: projects,
      isOverwriteMode: true,
      projectGroupId: existingGroup.id,
      projectGroupName: existingGroup.groupName,
      currentProjectName: this.projectModel.currentProject.name,
    }
    return await this.caeDataService.updateImportedCaeProjectGroup(dto);
  }

  private updateProjectDataLoaded(project: ProjectVO): void {
    if (project) {
      this.projectModel.updateProjectData(project);
      this.projectModel.currentProject.caeType = project.caeType;
      this.projectModel.currentProject.projectGroup = project.projectGroup;
    }
  }

  private findExistingProjectWithSameName(
    projectGroup: ProjectGroupVO,
  ): ProjectGroupVO | undefined {
    if (ProjectGroupUtils.isConfigurationGroup(projectGroup)) {
      return projectGroup;
    } else if (ProjectGroupUtils.isFolderGroup(projectGroup) || ProjectGroupUtils.isRootGroup(projectGroup)) {
      const groups = projectGroup.groups.filter(
        (p) =>
          p.groupName ===
          this.projectModel.currentProject.projectGroup.groupName,
      );
      if (groups.length > 0) {
        return groups[0];
      }
    }
    return undefined;
  }

  private async validateOverwrite(
    existingProjectGroup: ProjectGroupVO | undefined,
    projectsToSave: ImportedCaeProjectDTO[],
  ): Promise<SaveCaeButton> {
    if (existingProjectGroup == undefined) {
      return SaveCaeButton.NO_ACTION;
    }

    const config: Partial<SaveCaeProjectComponent> = {
      existingProjectGroup: existingProjectGroup,
      projectsToSave: projectsToSave,
    };

    return this.windowService.showModalMdFinish(SaveCaeProjectComponent, config);
  }
}
